import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  closestCenter,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";

import { useTranslation } from "react-i18next";
import { useMediaQuery } from "usehooks-ts";

import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import type { TDashboardScope } from "@/types/(company)/dashboardLayout.interface";
import { CanvasActionsContext, type ICanvasActions } from "./canvas-context";
import {
  WidgetEnvironmentContext,
  type IWidgetEnvironment,
} from "./widget-environment";
import { AddWidgetTile } from "./components/AddWidgetTile";
import { CanvasSkeleton } from "./components/CanvasSkeleton";
import { CommandBar } from "./components/CommandBar";
import { EmptyCanvas } from "./components/EmptyCanvas";
import { WidgetCard } from "./components/WidgetCard";
import { WidgetFrame } from "./components/WidgetFrame";
import { WidgetLibrary } from "./components/WidgetLibrary";
import { GRID_GAP, ROW_HEIGHT } from "./grid.constants";
import { spanForSize, stepSize } from "./grid.utils";
import { useDashboardLayout } from "./use-dashboard-layout";
import { useFreshness } from "./use-freshness";
import { useGridMetrics } from "./use-grid-metrics";
import { useWidgetResize } from "./use-widget-resize";
import type { IWidgetDefinition, IWidgetPlacement } from "./types";

interface DashboardGridProps {
  /** Which saved layout this canvas reads and writes. */
  scope: TDashboardScope;
  /** The dashboard's widget catalogue. */
  registry: IWidgetDefinition[];
  /** Arrangement used until the user saves one of their own. */
  defaultLayout: IWidgetPlacement[];
  title: string;
  description: string;
  /**
   * Re-reads the data behind every widget. Optional, and supplied by the page
   * rather than the engine: which API slices a dashboard draws on is the page's
   * knowledge, and a canvas that hard-coded them would stop being generic.
   */
  onRefresh?: () => void | Promise<void>;
  className?: string;
}

/**
 * A user-arrangeable widget canvas.
 *
 * Layout is order + size, never x/y. Each widget declares a footprint against a
 * 12-column grid and the browser packs them with `grid-auto-flow: dense`, so
 * removing or resizing a widget re-flows the rest for free — there is no stored
 * position to drift out of sync with what is on screen, and the same document
 * lays out correctly at every width.
 *
 * The canvas is as tall as its widgets and never scrolls on its own. An earlier
 * version pinned it to half the viewport, which on a 13" laptop left roughly
 * 250px — two rows, exactly what the default arrangement uses, so adding a
 * single widget started scrolling. It also made the largest size a user could
 * pick (two rows) fill the entire visible canvas, and it put a scroll container
 * inside a page that already scrolled. Letting the height follow the content
 * removes all three at once, at the price of the section below sometimes
 * sitting past the fold — which is what a detail layer is for.
 */
export const DashboardGrid = ({
  scope,
  registry,
  defaultLayout,
  title,
  description,
  onRefresh,
  className,
}: DashboardGridProps) => {
  const { t } = useTranslation(["common"]);
  const {
    placements,
    visiblePlacements,
    isLoading,
    availableWidgets,
    definitions,
    saveState,
    isCustomized,
    source,
    arrivingId,
    addWidget,
    removeWidget,
    resizeWidget,
    moveWidget,
    resetLayout,
    flush,
  } = useDashboardLayout({ scope, registry, defaultLayout });

  // One `matchMedia` subscription for every primitive on the canvas — see
  // widget-environment.ts for why this is not read per instance.
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );
  const widgetEnvironment = useMemo<IWidgetEnvironment>(
    () => ({ animationsEnabled: !prefersReducedMotion }),
    [prefersReducedMotion],
  );

  const {
    label: freshnessLabel,
    isRefreshing,
    refresh,
    canRefresh,
  } = useFreshness(onRefresh);

  const [isEditing, setIsEditing] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  /** The whole widgetable region — everything inside it counts as "not outside". */
  const sectionRef = useRef<HTMLElement | null>(null);
  const { ref, metrics, isMeasured } = useGridMetrics<HTMLDivElement>();

  // The resize gesture commits straight into the layout hook's setter, so the
  // snapped size lands on the same optimistic-then-debounced path as every other
  // edit — one persistence story, not two.
  const { session: resizeSession, start: startResize } = useWidgetResize({
    metrics,
    onCommit: resizeWidget,
  });

  const sensors = useSensors(
    // A short distance threshold keeps a click on a widget's own control from
    // being read as the start of a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  /**
   * Keeps the keyboard somewhere useful after a removal.
   *
   * Deleting the card that held focus otherwise drops the user back to the top of
   * the document, which for a keyboard user means re-tabbing through the whole
   * page to carry on arranging. Focus moves to the card that took its slot, or to
   * the add tile when the last one goes.
   */
  const pendingFocusRef = useRef<number | null>(null);
  useEffect(() => {
    const index = pendingFocusRef.current;
    if (index === null) return;
    pendingFocusRef.current = null;

    const canvas = ref.current;
    if (!canvas) return;

    // The card that took the removed one's slot, which is the card now AT that
    // index — clamped, because removing the last one leaves nothing there. An
    // earlier version grabbed the first grip on the canvas, which threw the
    // keyboard back to the top of the grid on every delete.
    const grips = Array.from(
      canvas.querySelectorAll<HTMLElement>("[data-widget-grip]"),
    );
    const successor = grips[Math.min(index, grips.length - 1)];
    const addTile = canvas.querySelector<HTMLElement>("[data-add-widget]");
    (successor ?? addTile)?.focus();
  }, [visiblePlacements, ref]);

  const handleRemove = useCallback(
    (instanceId: string) => {
      pendingFocusRef.current = visiblePlacements.findIndex(
        (placement) => placement.instanceId === instanceId,
      );
      removeWidget(instanceId);
    },
    [removeWidget, visiblePlacements],
  );

  /**
   * One object, created once, handed to every frame by context. Binding these
   * per widget in the render below is what used to defeat `React.memo` — four
   * fresh closures per card, on every canvas render.
   */
  const canvasActions = useMemo<ICanvasActions>(
    () => ({
      remove: handleRemove,
      resize: resizeWidget,
      step: (instanceId, definition, current, direction) =>
        resizeWidget(instanceId, stepSize(definition, current, direction)),
      beginResize: startResize,
    }),
    [handleRemove, resizeWidget, startResize],
  );

  const sortableIds = useMemo(
    () => visiblePlacements.map((placement) => placement.instanceId),
    [visiblePlacements],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggingId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      moveWidget(String(active.id), String(over.id));
    },
    [moveWidget],
  );

  /**
   * Entering by long press, rather than by the Customize button.
   *
   * Idempotent on purpose: a press that lands while the mode is already open —
   * the finger having strayed onto a second card, say — must not toggle it shut.
   */
  const enterEditing = useCallback(() => setIsEditing(true), []);

  /**
   * Leaving customize mode writes any queued edit immediately, so "Done" means
   * done rather than "done in a moment".
   */
  const exitEditing = useCallback(() => {
    setIsEditing(false);
    flush();
  }, [flush]);

  const toggleEditing = useCallback(() => {
    if (isEditing) {
      exitEditing();
      return;
    }
    setIsEditing(true);
  }, [exitEditing, isEditing]);

  /**
   * Escape leaves customize mode.
   *
   * Skipped while a resize gesture is running — Escape already means "abandon
   * this drag" there, and the narrower meaning has to win. Skipped while a dialog
   * or a menu is open for the same reason: the library drawer, the reset
   * confirmation, the actions kebab and each card's size menu all close on
   * Escape, and exiting the mode underneath them would be a second, invisible
   * consequence of one keypress.
   *
   * `[role='menu']` is the newer of the three. Radix dismisses a menu from a
   * *document*-level Escape listener without stopping propagation, so the event
   * reached this window listener too — closing the kebab, or a size menu, also
   * dropped the user out of customize mode.
   */
  useEffect(() => {
    if (!isEditing) return;

    const handleKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (resizeSession) return;
      if (
        document.querySelector(
          "[role='dialog'],[role='alertdialog'],[role='menu']",
        )
      )
        return;
      exitEditing();
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isEditing, resizeSession, exitEditing]);

  /**
   * Pressing anywhere off the canvas finishes and saves, the way tapping the
   * wallpaper leaves a home screen's jiggle.
   *
   * Scoped to the page's own `<main>` rather than to a list of things to ignore.
   * Every floating surface here — the widget library drawer, the reset
   * confirmation, tooltips, the undo toast — is portalled to `document.body`, so
   * a bare "not inside the canvas" test would treat clicking Undo as clicking
   * away and close the mode underneath it. Requiring the press to land inside
   * `main` excludes all of them at once, and keeps excluding whatever gets
   * portalled next.
   */
  useEffect(() => {
    if (!isEditing) return;

    const handlePointerDown = (event: PointerEvent) => {
      const section = sectionRef.current;
      const target = event.target;
      if (!section || !(target instanceof Node)) return;

      const pageRoot = section.closest("main");
      if (!pageRoot?.contains(target)) return;
      if (section.contains(target)) return;

      exitEditing();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isEditing, exitEditing]);
  /** Catalogue entries not yet on the canvas — drives the add tile's count. */
  const remainingCount = useMemo(
    () =>
      availableWidgets.filter(
        (widget) =>
          !(
            widget.singleton &&
            placements.some((placement) => placement.type === widget.type)
          ),
      ).length,
    [availableWidgets, placements],
  );

  const dragging = draggingId
    ? visiblePlacements.find((placement) => placement.instanceId === draggingId)
    : undefined;
  const draggingDefinition = dragging
    ? definitions.get(dragging.type)
    : undefined;

  /**
   * The overlay card is outside the grid, so it needs its footprint in pixels.
   * Computed from the same cell geometry the grid uses, which is what makes the
   * lifted card exactly the size of the hole it left.
   */
  const draggingBox = useMemo(() => {
    if (!dragging) return undefined;
    const span = spanForSize(dragging.size, metrics.columns);
    return {
      width: span.w * metrics.cellWidth + (span.w - 1) * GRID_GAP,
      height: span.h * ROW_HEIGHT + (span.h - 1) * GRID_GAP,
    };
  }, [dragging, metrics.cellWidth, metrics.columns]);

  return (
    <WidgetEnvironmentContext.Provider value={widgetEnvironment}>
      <CanvasActionsContext.Provider value={canvasActions}>
        <TooltipProvider delayDuration={400}>
          <section
            ref={sectionRef}
            aria-label={title}
            className={cn("flex flex-col", className)}
          >
            <p aria-live="polite" className="sr-only">
              {isEditing
                ? t("common:dashboard_grid.customize_mode_on")
                : t("common:dashboard_grid.customize_mode_off")}
            </p>

            <CommandBar
              title={title}
              description={description}
              isEditing={isEditing}
              saveState={saveState}
              widgetCount={visiblePlacements.length}
              availableCount={availableWidgets.length}
              isCustomized={isCustomized}
              source={source}
              freshnessLabel={freshnessLabel}
              isRefreshing={isRefreshing}
              canRefresh={canRefresh}
              onRefresh={refresh}
              onToggleEditing={toggleEditing}
              onOpenLibrary={() => setIsLibraryOpen(true)}
              onReset={resetLayout}
              onRetrySave={flush}
            />

            <div
              style={{
                // Read by .dashboard-canvas-editing to draw the column rhythm.
                ["--widget-columns" as string]: String(metrics.columns || 12),
                ["--widget-gap" as string]: `${GRID_GAP}px`,
              }}
              className={cn(
                "dashboard-canvas",
                // Top padding gives the chrome cluster, which straddles each card's
                // top edge, somewhere to sit on the first row.
                "px-2.5 pt-4 pb-2.5",
                isEditing && "dashboard-canvas-editing",
              )}
            >
              {/*
                The measured element is this inner track, not the padded canvas
                around it: `clientWidth` includes padding, so measuring the parent
                would over-report the grid width and make every cell too wide.
              */}
              <div ref={ref} className="min-w-0">
                {isLoading || !isMeasured ? (
                  <CanvasSkeleton
                    columns={metrics.columns || 12}
                    layout={defaultLayout}
                  />
                ) : visiblePlacements.length === 0 && !isEditing ? (
                  <EmptyCanvas onCustomize={() => setIsEditing(true)} />
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={(event: DragStartEvent) =>
                      setDraggingId(String(event.active.id))
                    }
                    onDragEnd={handleDragEnd}
                    onDragCancel={() => setDraggingId(null)}
                    // Widgets differ in size, so cached droppable rects go stale the
                    // moment one moves; re-measure continuously instead.
                    measuring={{
                      droppable: { strategy: MeasuringStrategy.Always },
                    }}
                    accessibility={{
                      announcements: {
                        onDragStart: ({ active }) =>
                          `Picked up ${nameFor(definitions, visiblePlacements, active.id)}. Use the arrow keys to move it, space to drop.`,
                        onDragOver: ({ active, over }) =>
                          over
                            ? `${nameFor(definitions, visiblePlacements, active.id)} is over position ${indexFor(visiblePlacements, over.id) + 1} of ${visiblePlacements.length}.`
                            : undefined,
                        onDragEnd: ({ active, over }) =>
                          over
                            ? `${nameFor(definitions, visiblePlacements, active.id)} dropped at position ${indexFor(visiblePlacements, over.id) + 1} of ${visiblePlacements.length}.`
                            : `${nameFor(definitions, visiblePlacements, active.id)} returned to its position.`,
                        onDragCancel: ({ active }) =>
                          `Moving ${nameFor(definitions, visiblePlacements, active.id)} was cancelled.`,
                      },
                    }}
                  >
                    <SortableContext
                      items={sortableIds}
                      strategy={rectSortingStrategy}
                    >
                      <div
                        role="list"
                        aria-label={`${title} widgets`}
                        className="grid"
                        style={{
                          gridTemplateColumns: `repeat(${metrics.columns}, minmax(0, 1fr))`,
                          gridAutoRows: `${ROW_HEIGHT}px`,
                          gridAutoFlow: "row dense",
                          gap: GRID_GAP,
                        }}
                      >
                        {visiblePlacements.map((placement, index) => {
                          const definition = definitions.get(placement.type);
                          if (!definition) return null;

                          const isResizingThis =
                            resizeSession?.instanceId === placement.instanceId;

                          return (
                            <WidgetFrame
                              key={placement.instanceId}
                              placement={placement}
                              definition={definition}
                              columns={metrics.columns}
                              isEditing={isEditing}
                              previewSize={
                                isResizingThis
                                  ? resizeSession.previewSize
                                  : null
                              }
                              resizeAxis={
                                isResizingThis ? resizeSession.axis : null
                              }
                              isArriving={arrivingId === placement.instanceId}
                              index={index}
                              onLongPress={enterEditing}
                            />
                          );
                        })}

                        {isEditing && (
                          <AddWidgetTile
                            columns={metrics.columns}
                            remaining={remainingCount}
                            onClick={() => setIsLibraryOpen(true)}
                          />
                        )}
                      </div>
                    </SortableContext>

                    {/*
                      The card follows the pointer in an overlay rather than being
                      translated inside the grid. Two things fall out of that: the
                      lifted card can break the grid's clipping and right angles (it
                      tilts and casts a real shadow), and the gap it left behind is a
                      genuine hole that tracks the drop index — which answers "where
                      will this land" directly instead of by inference.
                    */}
                    <DragOverlay
                      dropAnimation={{
                        duration: 220,
                        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
                        sideEffects: defaultDropAnimationSideEffects({
                          styles: { active: { opacity: "0.9" } },
                        }),
                      }}
                    >
                      {dragging && draggingDefinition && draggingBox ? (
                        <div style={draggingBox}>
                          <WidgetCard
                            definition={draggingDefinition}
                            size={dragging.size}
                            isEditing
                            variant="lifted"
                          />
                        </div>
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                )}
              </div>
            </div>

            <WidgetLibrary
              isOpen={isLibraryOpen}
              onClose={() => setIsLibraryOpen(false)}
              widgets={availableWidgets}
              placements={placements}
              onAdd={(type) => {
                addWidget(type);
                // Adding while not customizing would drop a widget the user cannot
                // then move — keep the two in step.
                setIsEditing(true);
              }}
            />
          </section>
        </TooltipProvider>
      </CanvasActionsContext.Provider>
    </WidgetEnvironmentContext.Provider>
  );
};

const nameFor = (
  definitions: Map<string, IWidgetDefinition>,
  placements: IWidgetPlacement[],
  id: string | number,
) => {
  const placement = placements.find((item) => item.instanceId === String(id));
  return placement
    ? (definitions.get(placement.type)?.name ?? "Widget")
    : "Widget";
};

const indexFor = (placements: IWidgetPlacement[], id: string | number) =>
  placements.findIndex((item) => item.instanceId === String(id));
